import React, { useState, useEffect } from 'react';
import { Offcanvas, Button } from 'react-bootstrap';
import { BiPencil } from "react-icons/bi";
import { BsX, BsPencil, BsCheck } from "react-icons/bs";
import { FaCogs, FaIdCard, FaNetworkWired, FaServer } from "react-icons/fa";
import LicenseInfoList from '../v360-license-info/LicenseInfoList';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Form } from 'react-bootstrap';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
  const token = userInfo?.token;
  const origin = localStorage.getItem('user-origin');
  return {
    Authorization: `Bearer ${token}`,
    'Tenant-Origin': origin
  };
};


const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

const SetupInfoDetails = ({ show, onHide, setupData, onEditSetup, activeExpiryFilter, defaultTab = 'licenseList', autoOpenLicenseId, onSetupUpdated }) => {
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [childModalOpen, setChildModalOpen] = useState(false);
    const [isEditingKey, setIsEditingKey] = useState(false);
    const [editedKey, setEditedKey] = useState("");
    
    const [isEditingClientName, setIsEditingClientName] = useState(false);
    const [editedClientName, setEditedClientName] = useState("");
    
    const [isEditingClientId, setIsEditingClientId] = useState(false);
    const [editedClientId, setEditedClientId] = useState("");
    
    // Add local state to reflect the update immediately without a full refresh
    const [localSetupData, setLocalSetupData] = useState(setupData);

    useEffect(() => {
        if (show) {
            setActiveTab(defaultTab);
            setChildModalOpen(false);
            setIsEditingKey(false);
            setIsEditingClientName(false);
            setIsEditingClientId(false);
            setLocalSetupData(setupData);
        }
    }, [show, defaultTab, setupData]);

    const handleSaveKey = async () => {
        try {
            await axios.patch(`${backendUrl}/api/v360-setup-info/${localSetupData.id}`, { key: editedKey }, { headers: getHeaders() });
            setLocalSetupData(prev => ({ ...prev, key: editedKey }));
            setIsEditingKey(false);
            Toast.fire({ icon: 'success', title: 'Key updated successfully' });
            if (onSetupUpdated) onSetupUpdated();
        } catch (error) {
            console.error('Error updating key', error);
            Toast.fire({ icon: 'error', title: 'Failed to update key' });
        }
    };

    const handleSaveClientName = async () => {
        try {
            await axios.patch(`${backendUrl}/api/v360-setup-info/${localSetupData.id}`, { client_name: editedClientName }, { headers: getHeaders() });
            setLocalSetupData(prev => ({ ...prev, client_name: editedClientName }));
            setIsEditingClientName(false);
            Toast.fire({ icon: 'success', title: 'Client Name updated successfully' });
            if (onSetupUpdated) onSetupUpdated();
        } catch (error) {
            console.error('Error updating client name', error);
            Toast.fire({ icon: 'error', title: 'Failed to update client name' });
        }
    };

    const handleSaveClientId = async () => {
        try {
            await axios.patch(`${backendUrl}/api/v360-setup-info/${localSetupData.id}`, { client_id: editedClientId }, { headers: getHeaders() });
            setLocalSetupData(prev => ({ ...prev, client_id: editedClientId }));
            setIsEditingClientId(false);
            Toast.fire({ icon: 'success', title: 'Client ID updated successfully' });
            if (onSetupUpdated) onSetupUpdated();
        } catch (error) {
            console.error('Error updating client ID', error);
            Toast.fire({ icon: 'error', title: 'Failed to update client ID' });
        }
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            style={{ 
                width: '1180px', 
                zIndex: 1045,
                visibility: childModalOpen ? 'hidden' : 'visible'
            }} 
            className="Setup-offcanvas"
            unmountOnExit={false}
        >
            {/* Offcanvas Header */}
            <Offcanvas.Header className="border-bottom bg-white py-3">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary rounded-circle p-2 me-3">
                            <FaCogs className="text-white" size={18} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">{localSetupData?.client_name || 'Setup Details'}</h5>
                            <small className="text-muted">Setup ID: {localSetupData?.id}</small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                                onEditSetup(setupData);
                            }}
                        >
                            <BiPencil className="me-1" />
                            Edit Setup
                        </Button>

                        <Button
                            variant="light"
                            size="sm"
                            onClick={onHide}
                            className="border-0"
                        >
                            <BsX size={20} />
                        </Button>
                    </div>
                </div>
            </Offcanvas.Header>

            {/* Offcanvas Body */}
            <Offcanvas.Body className="p-0 bg-light">
                {/* Summary Card */}
                <div className="p-4 border-bottom bg-white">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                                    <FaIdCard className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Key</label>
                                    <h6 className="mb-0 fw-semibold text-dark text-break">
                                        {localSetupData?.key || '-'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                                    <FaServer className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Setup Mode</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {setupData?.setup_mode || '-'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                                    <FaNetworkWired className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">IP Check</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {setupData?.ip_check ? 'Enabled' : 'Disabled'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white">
                    <ul className="nav nav-tabs nav-tabs-custom justify-content-start px-4 pt-2 font-size-13" role="tablist" style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${activeTab === 'licenseList' ? 'active' : ''}`}
                                onClick={() => handleTabClick('licenseList')}
                                role="tab"
                                style={{ cursor: 'pointer' }}
                            >
                                License Info
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                                onClick={() => handleTabClick('details')}
                                role="tab"
                                style={{ cursor: 'pointer' }}
                            >
                                Setup Details
                            </a>
                        </li>
                    </ul>

                    {/* Tab Content */}
                    <div className="tab-content">
                        {activeTab === 'licenseList' && (
                            <div className="tab-pane active" role="tabpanel">
                                <div className="p-3">
                                    <LicenseInfoList 
                                        setupId={setupData?.id} 
                                        isEmbedded={true} 
                                        activeExpiryFilter={activeExpiryFilter} 
                                        autoOpenLicenseId={autoOpenLicenseId} 
                                        closeParentOffcanvas={onHide}
                                        onChildModalToggle={setChildModalOpen}
                                    />
                                </div>
                            </div>
                        )}
                        {activeTab === 'details' && (
                            <div className="tab-pane active" role="tabpanel">
                                <div className="p-4 bg-light">
                                    <div className="card shadow-sm border-0 mb-4">
                                        <div className="card-header bg-white border-0 py-3">
                                            <div className="d-flex align-items-center">
                                                <FaCogs className="text-primary" />
                                                <h5 className="mb-0 ms-2 fw-semibold text-dark">Setup Configuration</h5>
                                            </div>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="row g-0">
                                                <div className="col-12">
                                                    <div className="p-3 border-bottom instance-detail-item h-100">
                                                        <div className="d-flex align-items-start">
                                                            <div className="text-muted me-3 mt-1"><FaIdCard size={14} /></div>
                                                            <div className="flex-grow-1">
                                                                <label className="text-muted small mb-1 d-block fw-medium">Key</label>
                                                                <div className="editable-container d-flex align-items-center">
                                                                    {isEditingKey ? (
                                                                        <div className="d-flex align-items-center w-100">
                                                                            <textarea
                                                                                value={editedKey}
                                                                                onChange={(e) => setEditedKey(e.target.value)}
                                                                                autoFocus
                                                                                rows={2}
                                                                                style={{ 
                                                                                    border: 'none', 
                                                                                    borderBottom: '1px solid #2F7EF7', 
                                                                                    background: 'transparent', 
                                                                                    outline: 'none',
                                                                                    padding: '0 0 2px 0',
                                                                                    margin: 0,
                                                                                    width: '100%',
                                                                                    minWidth: '200px',
                                                                                    fontSize: 'inherit',
                                                                                    fontWeight: '600',
                                                                                    color: '#343a40',
                                                                                    resize: 'none',
                                                                                    overflow: 'hidden'
                                                                                }}
                                                                            />
                                                                            <div className="d-flex flex-column ms-2">
                                                                                <BsCheck className="text-success mb-1" onClick={handleSaveKey} style={{ cursor: "pointer" }} size={22} />
                                                                                <BsX className="text-danger" onClick={() => setIsEditingKey(false)} style={{ cursor: "pointer" }} size={22} />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="hover1 w-100" onClick={() => { setEditedKey(localSetupData?.key || ''); setIsEditingKey(true); }}>
                                                                            <div className="text-dark fw-semibold text-break hover-field1 d-inline-block w-100" style={{ paddingLeft: 0 }}>
                                                                                {localSetupData?.key || '-'}
                                                                            </div>
                                                                            <BsPencil className="text-primary ms-2 pencil-icon1 flex-shrink-0" style={{ cursor: 'pointer' }} title="Edit Key" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="p-3 border-bottom border-end instance-detail-item h-100">
                                                        <div className="d-flex align-items-start">
                                                            <div className="text-muted me-3 mt-1"><FaServer size={14} /></div>
                                                            <div className="flex-grow-1">
                                                                <label className="text-muted small mb-1 d-block fw-medium">Client Name</label>
                                                                <div className="editable-container d-flex align-items-center">
                                                                    {isEditingClientName ? (
                                                                        <div className="d-flex align-items-center w-100">
                                                                            <textarea
                                                                                value={editedClientName}
                                                                                onChange={(e) => setEditedClientName(e.target.value)}
                                                                                autoFocus
                                                                                rows={1}
                                                                                style={{ 
                                                                                    border: 'none', 
                                                                                    borderBottom: '1px solid #2F7EF7', 
                                                                                    background: 'transparent', 
                                                                                    outline: 'none',
                                                                                    padding: '0 0 2px 0',
                                                                                    margin: 0,
                                                                                    width: '100%',
                                                                                    minWidth: '200px',
                                                                                    fontSize: 'inherit',
                                                                                    fontWeight: '600',
                                                                                    color: '#343a40',
                                                                                    resize: 'none',
                                                                                    overflow: 'hidden'
                                                                                }}
                                                                            />
                                                                            <div className="d-flex align-items-center ms-2">
                                                                                <BsCheck className="text-success mx-1" onClick={handleSaveClientName} style={{ cursor: "pointer" }} size={22} />
                                                                                <BsX className="text-danger mx-1" onClick={() => setIsEditingClientName(false)} style={{ cursor: "pointer" }} size={22} />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="hover1" onClick={() => { setEditedClientName(localSetupData?.client_name || ''); setIsEditingClientName(true); }}>
                                                                            <div className="text-dark fw-semibold text-break hover-field1 d-inline-block" style={{ paddingLeft: 0 }}>
                                                                                {localSetupData?.client_name || '-'}
                                                                            </div>
                                                                            <BsPencil className="text-primary ms-2 pencil-icon1" style={{ cursor: 'pointer' }} title="Edit Client Name" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                  <div className="col-md-6">
                                                    <div className="p-3 border-bottom instance-detail-item h-100">
                                                        <div className="d-flex align-items-start">
                                                            <div className="text-muted me-3 mt-1"><FaServer size={14} /></div>
                                                            <div className="flex-grow-1">
                                                                <label className="text-muted small mb-1 d-block fw-medium">Client ID</label>
                                                                <div className="editable-container d-flex align-items-center">
                                                                    {isEditingClientId ? (
                                                                        <div className="d-flex align-items-center w-100">
                                                                            <textarea
                                                                                value={editedClientId}
                                                                                onChange={(e) => setEditedClientId(e.target.value)}
                                                                                autoFocus
                                                                                rows={1}
                                                                                style={{ 
                                                                                    border: 'none', 
                                                                                    borderBottom: '1px solid #2F7EF7', 
                                                                                    background: 'transparent', 
                                                                                    outline: 'none',
                                                                                    padding: '0 0 2px 0',
                                                                                    margin: 0,
                                                                                    width: '100%',
                                                                                    minWidth: '200px',
                                                                                    fontSize: 'inherit',
                                                                                    fontWeight: '600',
                                                                                    color: '#343a40',
                                                                                    resize: 'none',
                                                                                    overflow: 'hidden'
                                                                                }}
                                                                            />
                                                                            <div className="d-flex align-items-center ms-2">
                                                                                <BsCheck className="text-success mx-1" onClick={handleSaveClientId} style={{ cursor: "pointer" }} size={22} />
                                                                                <BsX className="text-danger mx-1" onClick={() => setIsEditingClientId(false)} style={{ cursor: "pointer" }} size={22} />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="hover1" onClick={() => { setEditedClientId(localSetupData?.client_id || ''); setIsEditingClientId(true); }}>
                                                                            <div className="text-dark fw-semibold text-break hover-field1 d-inline-block" style={{ paddingLeft: 0 }}>
                                                                                {localSetupData?.client_id || '-'}
                                                                            </div>
                                                                            <BsPencil className="text-primary ms-2 pencil-icon1" style={{ cursor: 'pointer' }} title="Edit Client ID" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="p-3 border-bottom border-end instance-detail-item h-100">
                                                        <div className="d-flex align-items-start">
                                                            <div className="text-muted me-3 mt-1"><FaCogs size={14} /></div>
                                                            <div className="flex-grow-1">
                                                                <label className="text-muted small mb-1 d-block fw-medium">Setup Mode</label>
                                                                <div className="text-dark fw-semibold text-capitalize">{setupData?.setup_mode || '-'}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="p-3 border-bottom instance-detail-item h-100">
                                                        <div className="d-flex align-items-start">
                                                            <div className="text-muted me-3 mt-1"><FaNetworkWired size={14} /></div>
                                                            <div className="flex-grow-1">
                                                                <label className="text-muted small mb-1 d-block fw-medium">IP Check</label>
                                                                <div className="text-dark fw-semibold">
                                                                    <span className={`badge ${setupData?.ip_check ? 'bg-success' : 'bg-secondary'}`}>
                                                                        {setupData?.ip_check ? 'Enabled' : 'Disabled'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {setupData?.ip_check && (
                                                    <div className="col-12">
                                                        <div className="p-3 border-bottom instance-detail-item h-100">
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaNetworkWired size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">IP List</label>
                                                                    <div className="text-dark fw-semibold">{setupData?.ip_list ? setupData.ip_list.join(', ') : '-'}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                  <div className="col-md-6">
                                                      <div className="p-3 border-bottom border-end instance-detail-item h-100">
                                                          <div className="d-flex align-items-start">
                                                              <div className="text-muted me-3 mt-1"><FaCogs size={14} /></div>
                                                              <div className="flex-grow-1">
                                                                  <label className="text-muted small mb-1 d-block fw-medium">Setup Type</label>
                                                                  <div className="text-dark fw-semibold text-capitalize">{setupData?.setup_type || 'Production'}</div>
                                                              </div>
                                                          </div>
                                                      </div>
                                                  </div>
<div className="col-md-6">
                                                    <div className="p-3 border-end instance-detail-item h-100" style={{ borderBottom: 'none' }}>
                                                        <div className="d-flex align-items-start">
                                                            <div className="text-muted me-3 mt-1"><FaIdCard size={14} /></div>
                                                            <div className="flex-grow-1">
                                                                <label className="text-muted small mb-1 d-block fw-medium">Max Tenants</label>
                                                                <div className="text-dark fw-semibold">{setupData?.max_tenants || '0'}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="p-3 instance-detail-item h-100" style={{ borderBottom: 'none' }}>
                                                        <div className="d-flex align-items-start">
                                                            <div className="text-muted me-3 mt-1"><FaCogs size={14} /></div>
                                                            <div className="flex-grow-1">
                                                                <label className="text-muted small mb-1 d-block fw-medium">Max Process</label>
                                                                <div className="text-dark fw-semibold">{setupData?.max_process || '0'}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default SetupInfoDetails;
